import { TableCell, TableRow, Box } from '@material-ui/core'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@percent/lemonade'
import { ErrorView, Table } from '@percent/admin-dashboard/common/components'
import { useCurrencies } from '@percent/admin-dashboard/common/hooks'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { DisbursementDonationsTableProps } from './DisbursementDonationsTable.types'
import { DonationEnhanced } from '@percent/admin-dashboard/api/actions/donations/donations.types'
import styles from './DisbursementDonationsTable.module.scss'
import { JsonViewDrawer } from '@percent/admin-dashboard/common/components/drawer/JsonViewDrawer'
import { DisbursementDonationsActionsMenu } from './disbursementDonationsActionsMenu/DisbursementDonationsActionsMenu'

const cells = [
  { id: 'donation', isSortable: false, props: { width: '8%' } },
  { id: 'currency', isSortable: false, props: { width: '7%' } },
  { id: 'organization', isSortable: false, props: { width: '24%' } },
  { id: 'donor', isSortable: false, props: { width: '24%' } },
  { id: 'partner', isSortable: false, props: { width: '24%' } },
  { id: 'donationCreated', isSortable: false, props: { width: '13%' } }
]

export function DisbursementDonationsTable({
  isLoading,
  totalResults,
  data,
  previousPage,
  nextPage,
  errorMessage
}: DisbursementDonationsTableProps) {
  const { t } = useTranslation()
  const { currencyInfo } = useCurrencies()
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [selectedDonationDetails, setSelectedDonationDetails] = useState<DonationEnhanced | undefined>(undefined)
  const [isRawViewOpen, setIsRawViewOpen] = useState(false)

  const handleMenuBtnClick = (event: React.MouseEvent<HTMLElement>, donation: DonationEnhanced) => {
    setAnchorEl(event.currentTarget)
    setSelectedDonationDetails(donation)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Box className={styles.tableWrapper}>
      <Table
        isLoading={isLoading}
        totalResults={totalResults}
        data={data}
        previousPage={previousPage}
        nextPage={nextPage}
        columns={cells}
        orderBy="donationCreated"
        emptyTableText={t('table.emptyDonations')}
      >
        {data?.map((row, index) => {
          return (
            <TableRow key={row.id} data-testid="tableRow">
              <TableCell>
                <div>
                  {formatAmountFromMinorUnits({
                    currencyCode: row.amount.currency,
                    value: row.amount.amount,
                    currencies: currencyInfo
                  })}
                </div>
              </TableCell>
              <TableCell>
                <div>{row.amount.currency}</div>
              </TableCell>
              <TableCell>
                <div>{row.organisation?.displayName ?? row.organisation?.name ?? row.organisationId}</div>
              </TableCell>
              <TableCell>
                <div className={styles.donorWrapper}>
                  <span>
                    {row.firstName} {row.lastName}
                  </span>
                  <a className={styles.email} href={`mailto:${row.email}`}>
                    {row.email}
                  </a>
                </div>
              </TableCell>
              <TableCell>
                <div>{row.partner?.name ?? row.partnerId}</div>
              </TableCell>
              <TableCell>
                <div>{dayJS(row.createdAt).format('DD MMM YYYY, HH:mm')}</div>
              </TableCell>
              <TableCell align="right" style={{ padding: '16px 0' }}>
                <Box
                  className={styles.menuBtn}
                  onClick={event => handleMenuBtnClick(event, row)}
                  aria-controls="actions-menu"
                  aria-haspopup="true"
                  data-testid={`actions-button-${index}`}
                >
                  <Icon name="menu-horizontal" size="s" color="neutral400" />
                </Box>
                <DisbursementDonationsActionsMenu
                  anchorEl={anchorEl}
                  isOpen={selectedDonationDetails?.id === row.id && !!anchorEl}
                  onClose={handleMenuClose}
                  handleViewRaw={() => setIsRawViewOpen(true)}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </Table>
      {selectedDonationDetails ? (
        <JsonViewDrawer
          isOpen={isRawViewOpen}
          rawJsonData={selectedDonationDetails}
          onClose={() => {
            setIsRawViewOpen(false)
            setSelectedDonationDetails(undefined)
          }}
        />
      ) : null}
    </Box>
  )
}
